// @flow
import * as React from 'react'
import type { Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import { hasText } from 'utils/helpers'

import SingleLineName from '../../../ui/SingleLineName'
import ZentakeFormContext from '../ZentakeFormContext'

export function validateNameQuestion(
  questionId: string,
  value: string,
  setAnswerErrors: (p: any) => void,
  isRequired: boolean
): boolean {
  // eslint-disable-next-line no-unused-vars
  const [firstName, _, lastName] = value?.split(':') ?? []
  if (isRequired && !(hasText(firstName) && hasText(lastName))) {
    return false
  }
  return true
}

type Props = {
  question: Question,
  isOfficeUse: boolean,
  response: Response,
  sessions?: SessionObject,
  mode?: 'client' | 'office' | 'read-only' | 'preview',
}

function NameQuestion({
  question,
  isOfficeUse,
  mode,
  response,
  sessions,
}: Props): React.Node {
  const formContext = React.useContext(ZentakeFormContext)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)
  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const answer = formContext.getAnswer(question)

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [
    isDirty,
    question.is_required,
    isValid,
    questionError,
    answer?.answer_raw,
  ])

  function handleChange(nextValue: string) {
    setHasError(false)
    setErrorMessage('This field is required')
    formContext.onAnswerChanged(question, nextValue)
  }

  return (
    <SingleLineName
      disabled={readonly || officeUsePreview}
      errorLabel={errorMessage}
      hasError={hasError}
      isOfficeUse={isOfficeUse || officeUsePreview}
      isRequired={question.is_required}
      label={question.text}
      onChange={handleChange}
      questionId={question.id}
      response={response}
      sessions={sessions}
      showMiddleName={!!question?.include_middle_name}
      value={answer?.answer_raw}
      willShowNotAllowedCursor={willShowNotAllowedCursor}
    />
  )
}

export default NameQuestion
